import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Col, FormGroup, Modal, Row } from "reactstrap";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";
import { User } from "../../../../cores/session/interfaces/models/user";
import { useGlobalCore } from "../../../../cores/globals";
import { usePlanCore } from "../../../../cores/plan";
import { useUserCore } from "../../../../cores/user";
import { UserStatus } from "../../../../cores/user/constants/user-status";
import { InputWembii } from "../../InputWembii";
import { LabelWembii } from "../../LabelWembii";
import { LoadingSpinnerWembii } from "../../LoadingSpinnerWembii";
import { PaginationWemmbii } from "../../pagination/Pagination";
import { PlanBadge } from "../../companies/PlanBadge";
import "./ArchiveCandidatesModal.scss";

type DayOption = 30 | 60 | 90 | 120 | 180;

const INACTIVITY_OPTIONS: DayOption[] = [30, 60, 90, 120, 180];
const PAYMENT_OPTIONS: DayOption[] = [30, 60, 90, 120];

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onArchived: () => void;
}

export function ArchiveCandidatesModal({ isOpen, onClose, onArchived }: Props) {
  const intl = useIntl();
  const { isDarkMode } = useGlobalCore();
  const { plans, getAllPlans } = usePlanCore();
  const { getArchiveCandidates, bulkUpdateUserStatus } = useUserCore();

  const [inactivityDays, setInactivityDays] = useState<DayOption>(90);
  const [paymentGraceDays, setPaymentGraceDays] = useState<DayOption>(90);
  const [planId, setPlanId] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  const [loading, setLoading] = useState(false);
  const [candidates, setCandidates] = useState<User[]>([]);
  const [total, setTotal] = useState(0);
  const [allUserIds, setAllUserIds] = useState<string[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectAllMatching, setSelectAllMatching] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [archiving, setArchiving] = useState(false);

  useEffect(() => {
    if (isOpen) getAllPlans();
  }, [getAllPlans, isOpen]);

  const resetSelection = useCallback(() => {
    setSelectedIds([]);
    setSelectAllMatching(false);
    setConfirmText("");
  }, []);

  const loadCandidates = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getArchiveCandidates({
        inactivityDays,
        paymentGraceDays,
        planId: planId || undefined,
        page,
        perPage,
      });
      setCandidates(result.users || []);
      setTotal(result.total || 0);
      setAllUserIds(result.userIds || []);
      if (selectAllMatching) {
        setSelectedIds(result.userIds || []);
      }
    } catch (error) {
      console.error(error);
      toast.error(
        intl.formatMessage({
          id: "app.users.archiveCandidates.loadError",
          defaultMessage: "No se pudieron cargar los candidatos.",
        })
      );
    } finally {
      setLoading(false);
    }
  }, [
    getArchiveCandidates,
    inactivityDays,
    intl,
    page,
    paymentGraceDays,
    perPage,
    planId,
    selectAllMatching,
  ]);

  useEffect(() => {
    if (!isOpen) return;
    loadCandidates();
  }, [isOpen, loadCandidates]);

  useEffect(() => {
    if (!isOpen) {
      resetSelection();
      setConfirmOpen(false);
      setPage(1);
    }
  }, [isOpen, resetSelection]);

  const formatDateTime = (value?: string | null) => {
    if (!value) {
      return intl.formatMessage({
        id: "app.users.noLoginRecord",
        defaultMessage: "Sin registro",
      });
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "—";
    return intl.formatDate(date, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const lastSeenAt = (user: User) =>
    user.lastActiveAt || user.lastLoginAt || user.createdAt;

  const selectedCount = selectedIds.length;
  const confirmPhrase = `ARCHIVAR ${selectedCount}`;
  const confirmReady = confirmText.trim() === confirmPhrase;

  const pageAllSelected =
    candidates.length > 0 &&
    candidates.every(({ id }) => selectedIds.includes(id));

  const toggleUser = (userId: string) => {
    setSelectAllMatching(false);
    setSelectedIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const togglePage = () => {
    setSelectAllMatching(false);
    const pageIds = candidates.map(({ id }) => id);
    if (pageAllSelected) {
      setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
      return;
    }
    setSelectedIds((prev) => Array.from(new Set([...prev, ...pageIds])));
  };

  const onSelectAllMatching = () => {
    if (total === 0) return;
    if (selectAllMatching && selectedCount === total) {
      resetSelection();
      return;
    }
    setSelectAllMatching(true);
    setSelectedIds(allUserIds);
  };

  const handleArchive = async () => {
    if (!confirmReady || selectedCount === 0) return;
    setArchiving(true);
    try {
      const result = await bulkUpdateUserStatus({
        status: UserStatus.ARCHIVED.id,
        userIds: selectedIds,
        confirmCount: selectedCount,
        skipArchiveValidation: true,
      });
      toast.success(
        intl.formatMessage(
          {
            id: "app.users.archiveCandidates.success",
            defaultMessage: "{updated} usuarios archivados.",
          },
          { updated: result.updated }
        )
      );
      if (result.skipped?.length) {
        toast.warn(
          intl.formatMessage(
            {
              id: "app.users.archiveCandidates.skipped",
              defaultMessage: "{count} omitidos.",
            },
            { count: result.skipped.length }
          )
        );
      }
      setConfirmOpen(false);
      onClose();
      onArchived();
    } catch (error) {
      console.error(error);
      toast.error(
        intl.formatMessage({
          id: "app.users.archiveCandidates.archiveError",
          defaultMessage: "No se pudo completar el archivado.",
        })
      );
    } finally {
      setArchiving(false);
    }
  };

  const filterSummary = useMemo(
    () =>
      intl.formatMessage(
        {
          id: "app.users.archiveCandidates.summary",
          defaultMessage:
            "{total} candidatos · inactivos {inactivity}d · sin pago {payment}d",
        },
        { total, inactivity: inactivityDays, payment: paymentGraceDays }
      ),
    [inactivityDays, intl, paymentGraceDays, total]
  );

  return (
    <>
      <Modal
        isOpen={isOpen}
        toggle={() => !loading && !archiving && onClose()}
        size="xl"
        centered
        className="archive-candidates-modal"
        modalClassName={isDarkMode ? "archive-candidates-modal--dark" : undefined}
      >
        <div className="archive-candidates-modal__header">
          <h4 className="mb-0">
            {intl.formatMessage({
              id: "app.users.archiveCandidates.title",
              defaultMessage: "Archivar candidatos",
            })}
          </h4>
          <button
            type="button"
            className="archive-candidates-modal__close"
            onClick={onClose}
            disabled={loading || archiving}
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        <div className="archive-candidates-modal__body">
          <Row className="mb-3">
            <Col xs="12" md="4" lg="3">
              <FormGroup className="mb-md-0">
                <LabelWembii>
                  {intl.formatMessage({
                    id: "app.users.archiveCandidates.inactivity",
                    defaultMessage: "Último ingreso",
                  })}
                </LabelWembii>
                <InputWembii
                  type="select"
                  mb={false}
                  value={String(inactivityDays)}
                  onChange={(value: string) => {
                    resetSelection();
                    setPage(1);
                    setInactivityDays(Number(value) as DayOption);
                  }}
                >
                  {INACTIVITY_OPTIONS.map((days) => (
                    <option key={days} value={days}>
                      {intl.formatMessage(
                        {
                          id: "app.users.archiveCandidates.daysOrMore",
                          defaultMessage: "{days} días o más",
                        },
                        { days }
                      )}
                    </option>
                  ))}
                </InputWembii>
              </FormGroup>
            </Col>
            <Col xs="12" md="4" lg="3">
              <FormGroup className="mb-md-0">
                <LabelWembii>
                  {intl.formatMessage({
                    id: "app.users.archiveCandidates.noPayment",
                    defaultMessage: "Sin pago reciente",
                  })}
                </LabelWembii>
                <InputWembii
                  type="select"
                  mb={false}
                  value={String(paymentGraceDays)}
                  onChange={(value: string) => {
                    resetSelection();
                    setPage(1);
                    setPaymentGraceDays(Number(value) as DayOption);
                  }}
                >
                  {PAYMENT_OPTIONS.map((days) => (
                    <option key={days} value={days}>
                      {intl.formatMessage(
                        {
                          id: "app.users.archiveCandidates.daysOrMore",
                          defaultMessage: "{days} días o más",
                        },
                        { days }
                      )}
                    </option>
                  ))}
                </InputWembii>
              </FormGroup>
            </Col>
            <Col xs="12" md="4" lg="3">
              <FormGroup className="mb-md-0">
                <LabelWembii>
                  {intl.formatMessage({ id: "app.adminUsers.plan" })}
                </LabelWembii>
                <InputWembii
                  type="select"
                  mb={false}
                  value={planId}
                  onChange={(value: string) => {
                    resetSelection();
                    setPage(1);
                    setPlanId(value);
                  }}
                >
                  <option value="">
                    {intl.formatMessage({ id: "app.adminUsers.all" })}
                  </option>
                  {plans.map(({ id, name }) => (
                    <option key={id} value={String(id)}>
                      {intl.formatMessage({ id: name })}
                    </option>
                  ))}
                </InputWembii>
              </FormGroup>
            </Col>
          </Row>

          <div className="archive-candidates-modal__summary d-flex flex-wrap align-items-center justify-content-between mb-3">
            <span className="font-weight-bold">{filterSummary}</span>
            <div className="d-flex flex-wrap align-items-center" style={{ gap: "0.5rem" }}>
              <Button
                color="link"
                className="p-0"
                disabled={total === 0 || loading}
                onClick={onSelectAllMatching}
              >
                {selectAllMatching && selectedCount === total
                  ? intl.formatMessage({
                      id: "app.users.archiveCandidates.deselectAll",
                      defaultMessage: "Deseleccionar todos",
                    })
                  : intl.formatMessage(
                      {
                        id: "app.users.archiveCandidates.selectAll",
                        defaultMessage: "Seleccionar todos ({total})",
                      },
                      { total }
                    )}
              </Button>
              {selectedCount > 0 && (
                <span className="text-muted small">
                  {intl.formatMessage(
                    {
                      id: "app.users.bulk.selectedCount",
                      defaultMessage: "{count} usuarios seleccionados",
                    },
                    { count: selectedCount }
                  )}
                </span>
              )}
            </div>
          </div>

          {loading ? (
            <div className="py-5 text-center">
              <LoadingSpinnerWembii />
            </div>
          ) : total === 0 ? (
            <p className="text-muted text-center py-4 mb-0">
              {intl.formatMessage({
                id: "app.users.archiveCandidates.empty",
                defaultMessage: "No hay candidatos con estos filtros.",
              })}
            </p>
          ) : (
            <div className="archive-candidates-modal__table-wrap">
              <table className="archive-candidates-modal__table">
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        checked={pageAllSelected}
                        onChange={togglePage}
                        aria-label={intl.formatMessage({
                          id: "app.users.archiveCandidates.selectPage",
                          defaultMessage: "Seleccionar página",
                        })}
                      />
                    </th>
                    <th>ID</th>
                    <th>
                      {intl.formatMessage({ id: "app.adminUsers.name" })}
                    </th>
                    <th>Email</th>
                    <th>
                      {intl.formatMessage({ id: "app.adminUsers.phone" })}
                    </th>
                    <th>
                      {intl.formatMessage({
                        id: "app.users.accountCreated",
                        defaultMessage: "Cuenta creada",
                      })}
                    </th>
                    <th>
                      {intl.formatMessage({
                        id: "app.users.lastLogin",
                        defaultMessage: "Último ingreso",
                      })}
                    </th>
                    <th>
                      {intl.formatMessage({ id: "app.adminUsers.plan" })}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {candidates.map((user) => (
                    <tr key={user.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(user.id)}
                          onChange={() => toggleUser(user.id)}
                        />
                      </td>
                      <td>#{user.id}</td>
                      <td>
                        {user.firstName} {user.lastName}
                      </td>
                      <td>{user.email}</td>
                      <td>{user.phoneNumber || "—"}</td>
                      <td>{formatDateTime(user.createdAt)}</td>
                      <td>{formatDateTime(lastSeenAt(user))}</td>
                      <td>
                        {user.userPlan ? (
                          <PlanBadge plan={user.userPlan} show />
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {total > 0 && (
            <div className="mt-3">
              <PaginationWemmbii
                page={page}
                setPage={setPage}
                perPage={perPage}
                setPerPage={setPerPage}
                perPageOption={[25, 50, 100]}
                disabledPages={page * perPage >= total}
              />
            </div>
          )}
        </div>

        <div className="archive-candidates-modal__footer">
          <Button color="secondary" outline onClick={onClose} disabled={archiving}>
            {intl.formatMessage({
              id: "app.users.archiveCandidates.cancel",
              defaultMessage: "Cancelar",
            })}
          </Button>
          <Button
            color="warning"
            disabled={selectedCount === 0 || loading}
            onClick={() => {
              setConfirmText("");
              setConfirmOpen(true);
            }}
          >
            {intl.formatMessage(
              {
                id: "app.users.archiveCandidates.archiveSelected",
                defaultMessage: "Archivar seleccionados ({count})",
              },
              { count: selectedCount }
            )}
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={confirmOpen}
        toggle={() => !archiving && setConfirmOpen(false)}
        centered
        zIndex={1070}
        className="archive-candidates-confirm"
        modalClassName={isDarkMode ? "archive-candidates-modal--dark" : undefined}
      >
        <div className="p-4">
          <h5 className="mb-3">
            {intl.formatMessage({
              id: "app.users.archiveCandidates.confirmTitle",
              defaultMessage: "Confirmar archivado",
            })}
          </h5>
          <p className="mb-2">
            {intl.formatMessage(
              {
                id: "app.users.archiveCandidates.confirmBody",
                defaultMessage:
                  "Se archivarán {count} usuarios. Perderán acceso a la plataforma. Podés restaurarlos después desde el filtro Archivados.",
              },
              { count: selectedCount }
            )}
          </p>
          <p className="text-muted small mb-3">
            {intl.formatMessage(
              {
                id: "app.users.archiveCandidates.confirmHint",
                defaultMessage: 'Escribí "{phrase}" para confirmar.',
              },
              { phrase: confirmPhrase }
            )}
          </p>
          <InputWembii
            type="text"
            mb={false}
            value={confirmText}
            onChange={(value: string) => setConfirmText(value)}
            placeholder={confirmPhrase}
          />
          <div className="d-flex justify-content-end mt-4" style={{ gap: "0.75rem" }}>
            <Button
              color="secondary"
              outline
              onClick={() => setConfirmOpen(false)}
              disabled={archiving}
            >
              {intl.formatMessage({
              id: "app.users.archiveCandidates.cancel",
              defaultMessage: "Cancelar",
            })}
            </Button>
            <Button
              color="warning"
              disabled={!confirmReady || archiving}
              onClick={handleArchive}
            >
              {archiving
                ? intl.formatMessage({
                    id: "app.users.archiveCandidates.archiving",
                    defaultMessage: "Archivando…",
                  })
                : intl.formatMessage({
                    id: "app.users.archiveCandidates.confirmAction",
                    defaultMessage: "Archivar",
                  })}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
